import React from 'react'

function TankVisual({ lengthVal, widthVal, depthVal, shape, diameterVal, percent_level, inch_level, displayColor, alertColor }) {
    return (
        <>
            {/* Determine tank shape - default to cylinder if not specified */}
            {(shape === 'rectangle' || shape === 'square') ? (
                /* Rectangular/Square Tank SVG - with proportional dimensions */
                (() => {
                    // Use React state for hover
                    const [hovered, setHovered] = React.useState(false);

                    // Parse actual dimensions (remove " and convert to numbers)
                    const length = parseFloat(String(lengthVal || '84').replace(/"/g, '')) || 84;
                    const width = parseFloat(String(widthVal || '54').replace(/"/g, '')) || 54;
                    const depth = parseFloat(String(depthVal || '60').replace(/"/g, '')) || 60;

                    // Calculate proportional SVG dimensions
                    const maxFrontWidth = 160;
                    const maxHeight = 240;
                    const max3DDepth = 30; // Max perspective depth

                    // Scale factors
                    const lengthToWidthRatio = length / width;
                    const depthToLengthRatio = depth / length;
                    let frontWidth = maxFrontWidth;
                    let tankHeight = maxHeight;
                    let perspective3D = max3DDepth;

                    // Proportionality adjustments
                    if (depthToLengthRatio < 1) {
                        tankHeight = 240;
                    } else if (depthToLengthRatio > 1.5) {
                        frontWidth = Math.max(100, maxFrontWidth / depthToLengthRatio * 1.2);
                    }
                    perspective3D = Math.min(max3DDepth, Math.max(20, (width / length) * max3DDepth * 1.5));

                    const startX = 50;
                    const startY = 35;
                    const endX = startX + frontWidth;
                    const endY = startY + tankHeight;
                    const topRightX = endX + perspective3D;
                    const topRightY = startY - perspective3D * 0.5;

                    const fillPercent = Math.min(100, Math.max(0, percent_level || 0));
                    const liquidHeight = (fillPercent / 100) * tankHeight;
                    const liquidY = endY - liquidHeight;

                    // For highlighting/zoom effect
                    const dimTextZoom = hovered ? 1.55 : 1;
                    const dimColor = hovered ? (alertColor ?? "#FFE06C") : "#fff";

                    // Details box position (left side of tank, aligned to fill percent level)
                    const infoBoxWidth = 80;
                    const infoBoxHeight = 38;
                    const infoCircleX = startX - 24;
                    const infoCircleY = liquidY > (startY + 24) ? liquidY : startY + 24;
                    const infoBoxX = infoCircleX - infoBoxWidth - 14;
                    const infoBoxY = infoCircleY - infoBoxHeight / 2;
                    return (
                        <svg
                            viewBox="0 0 330 320"
                            width="100%"
                            height="100%"
                            preserveAspectRatio="xMidYMid meet"
                            style={{ cursor: "pointer" }}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                        >
                            <defs>
                                {/* Tank body gradient */}
                                <linearGradient id="rectTankBody" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#2a3356" />
                                    <stop offset="45%" stopColor="#202940" />
                                    <stop offset="75%" stopColor="#1a2035" />
                                    <stop offset="100%" stopColor="#151a2c" />
                                </linearGradient>
                                {/* Tank top */}
                                <linearGradient id="rectTankTop" x1="0%" y1="100%" x2="0%" y2="0%">
                                    <stop offset="0%" stopColor="#2f3763" />
                                    <stop offset="100%" stopColor="#202940" />
                                </linearGradient>
                                {/* Tank side */}
                                <linearGradient id="rectTankSide" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#1a2035" />
                                    <stop offset="100%" stopColor="#151a2c" />
                                </linearGradient>
                                {/* Liquid fill */}
                                <linearGradient id="rectLiquidFill" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor={displayColor} stopOpacity="0.95" />
                                    <stop offset="70%" stopColor={displayColor} stopOpacity="0.85" />
                                    <stop offset="100%" stopColor={displayColor} stopOpacity="0.75" />
                                </linearGradient>
                                {/* Liquid surface */}
                                <linearGradient id="rectLiquidTop" x1="0%" y1="100%" x2="0%" y2="0%">
                                    <stop offset="0%" stopColor={displayColor} stopOpacity="0.85" />
                                    <stop offset="100%" stopColor={displayColor} stopOpacity="0.15" />
                                </linearGradient>
                                <clipPath id="rectClip">
                                    <rect
                                        x={startX}
                                        y={startY}
                                        width={frontWidth}
                                        height={tankHeight}
                                    />
                                </clipPath>
                                {/* Glow for info circle */}
                                <radialGradient id="infoDotGlowLeft" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#fff8b7" stopOpacity="1" />
                                    <stop offset="100%" stopColor="#ffd400" stopOpacity="0" />
                                </radialGradient>
                                {/* Info box shadow */}
                                <filter id="boxShadowLeft" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.25" />
                                </filter>
                            </defs>

                            {/* Top face */}
                            <polygon
                                points={`${startX},${startY} ${endX},${startY} ${topRightX},${topRightY} ${startX + perspective3D},${topRightY}`}
                                fill="url(#rectTankTop)"
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Right side */}
                            <polygon
                                points={`${endX},${startY} ${topRightX},${topRightY} ${topRightX},${endY - perspective3D * 0.4} ${endX},${endY}`}
                                fill="url(#rectTankSide)"
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Right side liquid */}
                            {fillPercent > 0 && (
                                <polygon
                                    points={`${endX},${liquidY} ${topRightX},${liquidY - perspective3D * 0.4} ${topRightX},${endY - perspective3D * 0.4} ${endX},${endY}`}
                                    fill={displayColor}
                                    opacity="0.7"
                                />
                            )}

                            {/* Front face */}
                            <rect
                                x={startX}
                                y={startY}
                                width={frontWidth}
                                height={tankHeight}
                                fill="url(#rectTankBody)"
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Liquid fill */}
                            <g clipPath="url(#rectClip)">
                                <rect
                                    x={startX}
                                    y={liquidY}
                                    width={frontWidth}
                                    height={liquidHeight}
                                    fill="url(#rectLiquidFill)"
                                    style={{ transition: "all 1s ease" }}
                                />

                                {/* Liquid surface */}
                                {fillPercent > 0 && fillPercent < 100 && (
                                    <polygon
                                        points={`${startX},${liquidY} ${endX},${liquidY} ${topRightX},${liquidY - perspective3D * 0.4} ${startX + perspective3D},${liquidY - perspective3D * 0.4}`}
                                        fill="url(#rectLiquidTop)"
                                    />
                                )}
                            </g>

                            {/* Level markers */}
                            {[0, 25, 50, 75, 100].map(level => {
                                const y = endY - (level / 100) * tankHeight;
                                return (
                                    <g key={level} opacity="0.7">
                                        <line
                                            x1={startX - 16}
                                            y1={y}
                                            x2={startX - 6}
                                            y2={y}
                                            stroke="#ffffff"
                                            strokeWidth="1.5"
                                        />
                                        <text
                                            x={startX - 20}
                                            y={y + 4}
                                            fill="#ffffff"
                                            fontSize="16"
                                            textAnchor="end"
                                        >
                                            {level}%
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Sensor indicator on top - vertical probe */}
                            {(() => {
                                const sensorX = (startX + endX) / 2 + perspective3D / 2;
                                const sensorTopY = topRightY - 8;
                                const sensorHeight = 18;
                                return (
                                    <g>
                                        {/* Sensor housing/cap */}
                                        <rect x={sensorX - 4} y={sensorTopY} width={8} height={6} fill="#0D2513" stroke="#b2b8de" strokeWidth="1.5" rx="1" />
                                        {/* Sensor probe going down into tank */}
                                        <rect x={sensorX - 2} y={sensorTopY + 6} width={4} height={sensorHeight} fill="#1a3f2b" stroke="#b2b8de" strokeWidth="1" />
                                        {/* LED light on top with glow */}
                                        <circle cx={sensorX} cy={sensorTopY - 3} r="3" fill="#0D2513" stroke="#b2b8de" strokeWidth="1.5" />
                                        <circle cx={sensorX} cy={sensorTopY - 3} r="1.5" fill="#b2b8de">
                                            <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />
                                        </circle>
                                        {/* Vertical scanning beam inside probe */}
                                        <rect x={sensorX - 1} y={sensorTopY + 8} width={2} height={4} fill="#b2b8de" opacity="0.6">
                                            <animate attributeName="y" values={`${sensorTopY + 8};${sensorTopY + sensorHeight};${sensorTopY + 8}`} dur="2s" repeatCount="indefinite" />
                                        </rect>
                                    </g>
                                );
                            })()}

                            {/* Inlet pipe - positioned at ~90% level (10% from top) */}
                            {/* ... */}

                            {/* Animated info point & percent box on hover to the left */}
                            {hovered && (
                                <>
                                    {/* Animated glowing dot */}
                                    <g>
                                        <circle
                                            cx={infoCircleX}
                                            cy={infoCircleY}
                                            r="12"
                                            fill="url(#infoDotGlowLeft)"
                                            opacity="0.88"
                                        >
                                            <animate
                                                attributeName="r"
                                                values="11;16;11"
                                                dur="1.2s"
                                                repeatCount="indefinite"
                                            />
                                            <animate
                                                attributeName="opacity"
                                                values="0.45;0.88;0.45"
                                                dur="1.2s"
                                                repeatCount="indefinite"
                                            />
                                        </circle>
                                        <circle
                                            cx={infoCircleX}
                                            cy={infoCircleY}
                                            r="5"
                                            fill={alertColor ?? "#ffe06c"}
                                            stroke="#d6b100"
                                            strokeWidth="1.6"
                                        >
                                            <animate
                                                attributeName="r"
                                                values="5;7;5"
                                                dur="1.2s"
                                                repeatCount="indefinite"
                                            />
                                        </circle>
                                    </g>
                                    {/* Info box */}
                                    <g filter="url(#boxShadowLeft)">
                                        <rect
                                            x={infoBoxX}
                                            y={infoBoxY}
                                            rx={6}
                                            ry={6}
                                            width={infoBoxWidth}
                                            height={infoBoxHeight}
                                            fill="#22243c"
                                            stroke={alertColor ?? "#ffe06c"}
                                            strokeWidth="1.5"
                                        />
                                        <text
                                            x={infoBoxX + infoBoxWidth / 2}
                                            y={infoBoxY + 25}
                                            textAnchor="middle"
                                            fontSize="19"
                                            fill={alertColor ?? "#ffe06c"}
                                            fontFamily="monospace"
                                            fontWeight="bold"
                                        >
                                            {`${fillPercent}%`}
                                        </text>
                                    </g>

                                    {/* Inch box */}
                                    {
                                        inch_level &&
                                        <g filter="url(#boxShadowLeft)">
                                            <text
                                                x={infoBoxX + infoBoxWidth / 0.4}
                                                // y={infoBoxY + 25}
                                                y={infoBoxHeight * 4}
                                                textAnchor="middle"
                                                fontSize="19"
                                                fill={alertColor ?? "#ffe06c"}
                                                fontFamily="monospace"
                                                fontWeight="bold"
                                            >
                                                {`${inch_level}"`}
                                            </text>
                                        </g>
                                    }
                                </>
                            )}

                            {/* Dimensions -- zoom and highlight if hovered */}
                            <text
                                x={(startX + endX) / 2}
                                y={endY + 34}
                                fill={dimColor}
                                fontSize={14 * dimTextZoom}
                                textAnchor="middle"
                                fontWeight={hovered ? "bold" : undefined}
                                style={{
                                    // filter: hovered ? "drop-shadow(0 2px 8px #ffe06c77)" : undefined,
                                    transition: "all 0.25s cubic-bezier(.47,1.64,.41,.8)"
                                }}
                            >
                                {length}" L
                            </text>

                            <text
                                x={topRightX - perspective3D / 2}
                                y={topRightY - 6}
                                fill={dimColor}
                                fontSize={14 * dimTextZoom}
                                textAnchor="middle"
                                fontWeight={hovered ? "bold" : undefined}
                                style={{
                                    // filter: hovered ? "drop-shadow(0 2px 8px #ffe06c77)" : undefined,
                                    transition: "all 0.25s cubic-bezier(.47,1.64,.41,.8)"
                                }}
                            >
                                {width}" W
                            </text>

                            <text
                                x={endX + 56}
                                y={(startY + endY) / 2}
                                fill={dimColor}
                                fontSize={14 * dimTextZoom}
                                dominantBaseline="middle"
                                fontWeight={hovered ? "bold" : undefined}
                                style={{
                                    // filter: hovered ? "drop-shadow(0 2px 8px #ffe06c77)" : undefined,
                                    transition: "all 0.25s cubic-bezier(.47,1.64,.41,.8)"
                                }}
                            >
                                {depth}" D
                            </text>
                        </svg>
                    );
                })()
            ) : (shape === 'circle' || shape === 'cylinder_vertical') ? (
                /* Cylindrical Tank SVG (default) - with proportional dimensions */
                (() => {
                    // Use React state for hover
                    const [hovered, setHovered] = React.useState(false);
                    // Parse actual dimensions - ensure they're strings before calling replace
                    const diameterValue = diameterVal || '48';
                    const depthValue = depthVal || '60';
                    const diameterStr = String(diameterValue).replace(/"/g, '').trim() || '48';
                    const depthStr = String(depthValue).replace(/"/g, '').trim() || '60';
                    const diameter = parseFloat(diameterStr) || 48;
                    const depth = parseFloat(depthStr) || 60;

                    // Calculate proportional SVG dimensions
                    const maxWidth = 160;
                    const maxHeight = 240;

                    // Scale based on diameter to depth ratio
                    const aspectRatio = depth / diameter;

                    let tankWidth = maxWidth;
                    let tankHeight = maxHeight;

                    if (aspectRatio < 1) {
                        // Tank is wider than tall (squat cylinder)
                        // tankHeight = Math.max(100, maxHeight * aspectRatio);
                    } else if (aspectRatio > 2) {
                        // Tank is much taller than wide (tall cylinder)
                        tankWidth = Math.max(80, maxWidth / aspectRatio * 1.5);
                    }
                    const centerX = 140; // Increased from 120 for left margin
                    const radiusX = tankWidth / 2;
                    const radiusY = Math.min(25, radiusX * 0.3); // Ellipse for 3D effect
                    const startX = 50;
                    const startY = 35;
                    const endY = startY + tankHeight;

                    const fillPercent = Math.min(100, Math.max(0, percent_level || 0));
                    const liquidHeight = (fillPercent / 100) * tankHeight;
                    const liquidY = endY - liquidHeight;

                    // For highlighting/zoom effect
                    const dimTextZoom = hovered ? 1.55 : 1;
                    const dimColor = hovered ? (alertColor ?? "#FFE06C") : "#fff";

                    // Details box position (left side of tank, aligned to fill percent level)
                    const infoBoxWidth = 80;
                    const infoBoxHeight = 38;
                    const infoCircleX = startX - 24;
                    const infoCircleY = liquidY > (startY + 24) ? liquidY : startY + 24;
                    const infoBoxX = infoCircleX - infoBoxWidth - 14;
                    const infoBoxY = infoCircleY - infoBoxHeight / 2;

                    return (
                        <svg
                            viewBox="0 0 350 320"
                            width="100%"
                            height="100%"
                            preserveAspectRatio="xMidYMid meet"
                            style={{ cursor: "pointer" }}
                            onMouseEnter={() => setHovered(true)}
                            onMouseLeave={() => setHovered(false)}
                        >
                            <defs>
                                {/* Tank body gradient */}
                                <linearGradient id="tankBody" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#2a3356" />
                                    <stop offset="40%" stopColor="#202940" />
                                    <stop offset="75%" stopColor="#1a2035" />
                                    <stop offset="100%" stopColor="#151a2c" />
                                </linearGradient>

                                {/* Liquid gradient */}
                                <linearGradient id="liquidFill" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor={displayColor} stopOpacity="0.95" />
                                    <stop offset="70%" stopColor={displayColor} stopOpacity="0.85" />
                                    <stop offset="100%" stopColor="#8f96c8" stopOpacity="0.75" />
                                </linearGradient>

                                {/* Clip tank shape */}
                                <clipPath id="tankClip">
                                    <path
                                        d={`
                                            M${centerX - radiusX},${startY + radiusY}
                                            L${centerX - radiusX},${endY - radiusY}
                                            Q${centerX - radiusX},${endY + radiusY} ${centerX},${endY + radiusY}
                                            Q${centerX + radiusX},${endY + radiusY} ${centerX + radiusX},${endY - radiusY}
                                            L${centerX + radiusX},${startY + radiusY}
                                            Q${centerX + radiusX},${startY - radiusY} ${centerX},${startY - radiusY}
                                            Q${centerX - radiusX},${startY - radiusY} ${centerX - radiusX},${startY + radiusY}
                                            Z
                                        `}
                                    />
                                </clipPath>
                            </defs>

                            {/* Tank body */}
                            <path
                                d={`
                                    M${centerX - radiusX},${startY + radiusY}
                                    L${centerX - radiusX},${endY - radiusY}
                                    Q${centerX - radiusX},${endY + radiusY} ${centerX},${endY + radiusY}
                                    Q${centerX + radiusX},${endY + radiusY} ${centerX + radiusX},${endY - radiusY}
                                    L${centerX + radiusX},${startY + radiusY}
                                    Q${centerX + radiusX + 5},${startY - radiusY} ${centerX},${startY - radiusY}
                                    Q${centerX - radiusX - 5},${startY - radiusY} ${centerX - radiusX},${startY + radiusY}
                                `}
                                fill="url(#tankBody)"
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Bottom ellipse */}
                            <ellipse
                                cx={centerX}
                                cy={endY - radiusY}
                                rx={radiusX}
                                ry={radiusY}
                                fill="#151a2c"
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Liquid fill */}
                            <g clipPath="url(#tankClip)">
                                <rect
                                    x={centerX - radiusX}
                                    y={liquidY}
                                    width={tankWidth}
                                    height={liquidHeight + radiusY}
                                    fill="url(#liquidFill)"
                                    style={{ transition: "all 1s ease" }}
                                />

                                {/* Liquid surface */}
                                {fillPercent > 0 && fillPercent < 100 && (
                                    <>
                                        <ellipse
                                            cx={centerX}
                                            cy={liquidY}
                                            rx={radiusX - 2}
                                            ry={radiusY - 1}
                                            fill={displayColor}
                                            opacity="0.9"
                                        />
                                        <ellipse
                                            cx={centerX}
                                            cy={liquidY - 1}
                                            rx={radiusX - 12}
                                            ry={radiusY - 5}
                                            fill="#ffffff"
                                            opacity="0.08"
                                        />
                                    </>
                                )}
                            </g>

                            {/* Tank top */}
                            <ellipse
                                cx={centerX}
                                cy={startY}
                                rx={radiusX}
                                ry={radiusY}
                                fill="#202940"
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Side outlines */}
                            <line
                                x1={centerX - radiusX}
                                y1={startY + radiusY}
                                x2={centerX - radiusX}
                                y2={endY - radiusY}
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />
                            <line
                                x1={centerX + radiusX}
                                y1={startY + radiusY}
                                x2={centerX + radiusX}
                                y2={endY - radiusY}
                                stroke="#2f3763"
                                strokeWidth="1.5"
                            />

                            {/* Level markers */}
                            {[0, 25, 50, 75, 100].map(level => {
                                const y = endY - (level / 100) * tankHeight;
                                return (
                                    <g key={level} opacity="0.7">
                                        <line
                                            x1={centerX - radiusX - 16}
                                            y1={y}
                                            x2={centerX - radiusX - 6}
                                            y2={y}
                                            stroke="#ffffff"
                                            strokeWidth="1.5"
                                        />
                                        <text
                                            x={centerX - radiusX - 20}
                                            y={y + 4}
                                            fill="#ffffff"
                                            fontSize="16"
                                            textAnchor="end"
                                        >
                                            {level}%
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Sensor indicator - vertical probe */}
                            {(() => {
                                const sensorX = centerX;
                                const sensorTopY = startY - radiusY - 8;
                                const sensorHeight = 20;
                                return (
                                    <g>
                                        {/* Sensor housing/cap */}
                                        <rect x={sensorX - 5} y={sensorTopY} width={10} height={8} fill="#0D2513" stroke="#b2b8de" strokeWidth="1.5" rx="2" />
                                        {/* Sensor probe going down into tank */}
                                        <rect x={sensorX - 2.5} y={sensorTopY + 8} width={5} height={sensorHeight} fill="#1a3f2b" stroke="#b2b8de" strokeWidth="1" />
                                        {/* LED light on top with glow */}
                                        <circle cx={sensorX} cy={sensorTopY - 4} r="3.5" fill="#0D2513" stroke="#b2b8de" strokeWidth="1.5" />
                                        <circle cx={sensorX} cy={sensorTopY - 4} r="2" fill="#b2b8de">
                                            <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />
                                        </circle>
                                        {/* Vertical scanning beam inside probe */}
                                        <rect x={sensorX - 1} y={sensorTopY + 10} width={2} height={4} fill="#b2b8de" opacity="0.6">
                                            <animate attributeName="y" values={`${sensorTopY + 10};${sensorTopY + sensorHeight + 4};${sensorTopY + 10}`} dur="2s" repeatCount="indefinite" />
                                        </rect>
                                    </g>
                                );
                            })()}

                            {/* Inlet pipe - positioned at ~90% level (10% from top) */}
                            {/* {(() => {
                                const inletY = startY + (tankHeight * 0.10); // 10% from top
                                const pipeWidth = 30;
                                const pipeHeight = 14;
                                return (
                                    <g>
                                        <rect
                                            x={centerX - radiusX - pipeWidth}
                                            y={inletY - pipeHeight / 2}
                                            width={pipeWidth + 5}
                                            height={pipeHeight}
                                            fill="#4a5568"
                                            stroke="#718096"
                                            strokeWidth="2"
                                            rx="2"
                                        />
                                        <ellipse
                                            cx={centerX - radiusX - pipeWidth}
                                            cy={inletY}
                                            rx={4}
                                            ry={pipeHeight / 2 - 1}
                                            fill="#1a202c"
                                            stroke="#2d3748"
                                            strokeWidth="1"
                                        />
                                        <text x={centerX - radiusX - pipeWidth - 6} y={inletY - pipeHeight / 2 - 3} fill="#a0aec0" fontSize="8" textAnchor="end" className="font-mono">INLET</text>
                                    </g>
                                );
                            })()} */}

                            {/* Animated info point & percent box on hover to the left */}
                            {hovered && (
                                <>
                                    {/* Animated glowing dot */}
                                    <g>
                                        <circle
                                            cx={infoCircleX}
                                            cy={infoCircleY}
                                            r="12"
                                            fill="url(#infoDotGlowLeft)"
                                            opacity="0.88"
                                        >
                                            <animate
                                                attributeName="r"
                                                values="11;16;11"
                                                dur="1.2s"
                                                repeatCount="indefinite"
                                            />
                                            <animate
                                                attributeName="opacity"
                                                values="0.45;0.88;0.45"
                                                dur="1.2s"
                                                repeatCount="indefinite"
                                            />
                                        </circle>
                                        <circle
                                            cx={infoCircleX}
                                            cy={infoCircleY}
                                            r="5"
                                            fill={alertColor ?? "#ffe06c"}
                                            stroke="#d6b100"
                                            strokeWidth="1.6"
                                        >
                                            <animate
                                                attributeName="r"
                                                values="5;7;5"
                                                dur="1.2s"
                                                repeatCount="indefinite"
                                            />
                                        </circle>
                                    </g>
                                    {/* Info box */}
                                    <g filter="url(#boxShadowLeft)">
                                        <rect
                                            x={infoBoxX}
                                            y={infoBoxY}
                                            rx={6}
                                            ry={6}
                                            width={infoBoxWidth}
                                            height={infoBoxHeight}
                                            fill="#22243c"
                                            stroke={alertColor ?? "#ffe06c"}
                                            strokeWidth="1.5"
                                        />
                                        <text
                                            x={infoBoxX + infoBoxWidth / 2}
                                            y={infoBoxY + 25}
                                            textAnchor="middle"
                                            fontSize="19"
                                            fill={alertColor ?? "#ffe06c"}
                                            fontFamily="monospace"
                                            fontWeight="bold"
                                        >
                                            {`${fillPercent}%`}
                                        </text>
                                    </g>

                                    {/* Inch box */}
                                    {
                                        inch_level &&
                                        <g filter="url(#boxShadowLeft)">
                                            <text
                                                x={infoBoxX + infoBoxWidth / 0.4}
                                                // y={infoBoxY + 25}
                                                y={infoBoxHeight * 4}
                                                textAnchor="middle"
                                                fontSize="19"
                                                fill={alertColor ?? "#ffe06c"}
                                                fontFamily="monospace"
                                                fontWeight="bold"
                                            >
                                                {`${inch_level}"`}
                                            </text>
                                        </g>
                                    }
                                </>
                            )}

                            {/* Diameter label */}
                            <text
                                x={centerX}
                                y={endY + 36}
                                fill={dimColor}
                                fontSize={14 * dimTextZoom}
                                textAnchor="middle"
                                fontWeight={hovered ? "bold" : undefined}
                            >
                                ⌀ {diameter}"
                            </text>

                            {/* Depth label */}
                            <text
                                x={centerX + radiusX + 58}
                                y={(startY + endY) / 2}
                                fill={dimColor}
                                fontSize={14 * dimTextZoom}
                                dominantBaseline="middle"
                                fontWeight={hovered ? "bold" : undefined}
                            >
                                {depth}" D
                            </text>
                        </svg>
                    );
                })()
            ) : (() => {
                const [hovered, setHovered] = React.useState(false);

                const diameter = parseFloat(String(diameterVal || '48').replace(/"/g, '')) || 48;
                const length = parseFloat(String(lengthVal || '96').replace(/"/g, '')) || 96;

                const maxLength = 260;
                const maxHeight = 120;

                const tankLength = maxLength;
                const tankHeight = Math.min(maxHeight, (diameter / length) * maxLength * 2);
                const radiusX = tankHeight / 2;
                const radiusY = radiusX * 0.55;

                const startX = 40;
                const centerY = 160;
                const endX = startX + tankLength;

                const fillPercent = Math.min(100, Math.max(0, percent_level || 0));
                const liquidHeight = (fillPercent / 100) * (radiusX * 2);
                const liquidY = centerY + radiusX - liquidHeight;


                const dimColor = hovered ? (alertColor ?? "#FFE06C") : "#fff";
                const dimTextZoom = hovered ? 1.2 : 1;

                // ---- Hover info positioning (horizontal cylinder) ----
                const infoBoxWidth = 80;
                const infoBoxHeight = 38;

                // Info dot follows liquid front (X axis), vertically centered
                const infoCircleX = startX - 16;       // left side of tank
                const infoCircleY = liquidY;


                // Keep info box to the left of the dot
                const infoBoxX = infoCircleX - infoBoxWidth - 18;
                const infoBoxY = infoCircleY - infoBoxHeight / 2;

                const tankRadius = radiusX;
                const R = tankRadius;
                const h = Math.max(0, Math.min(liquidHeight, R * 2));

                // Distance from circle center to liquid surface
                const y = R - h;

                // Clamp for acos safety
                const clamped = Math.max(-1, Math.min(1, y / R));
                const theta = Math.acos(clamped);

                // Horizontal extent of liquid inside circular ends
                const xOffset = R * Math.sin(theta);


                return (
                    <svg
                        viewBox="0 0 360 300"
                        width="100%"
                        height="100%"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        <defs>
                            <linearGradient id="tankBodyH" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#2a3356" />
                                <stop offset="100%" stopColor="#151a2c" />
                            </linearGradient>

                            {/* Liquid gradient */}
                            <linearGradient id="liquidFill" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={displayColor} stopOpacity="0.95" />
                                <stop offset="70%" stopColor={displayColor} stopOpacity="0.85" />
                                <stop offset="100%" stopColor="#8f96c8" stopOpacity="0.75" />
                            </linearGradient>

                            <clipPath id="tankClipH">
                                <path
                                    d={`
                                        M${startX + radiusX},${centerY - radiusX}
                                        L${endX - radiusX},${centerY - radiusX}
                                        Q${endX},${centerY - radiusX} ${endX},${centerY}
                                        Q${endX},${centerY + radiusX} ${endX - radiusX},${centerY + radiusX}
                                        L${startX + radiusX},${centerY + radiusX}
                                        Q${startX},${centerY + radiusX} ${startX},${centerY}
                                        Q${startX},${centerY - radiusX} ${startX + radiusX},${centerY - radiusX}
                                        Z
                                    `}
                                />
                            </clipPath>
                        </defs>

                        {/* Tank body */}
                        <path
                            d={`
                                M${startX + radiusX},${centerY - radiusX}
                                L${endX - radiusX},${centerY - radiusX}
                                Q${endX},${centerY - radiusX} ${endX},${centerY}
                                Q${endX},${centerY + radiusX} ${endX - radiusX},${centerY + radiusX}
                                L${startX + radiusX},${centerY + radiusX}
                                Q${startX},${centerY + radiusX} ${startX},${centerY}
                                Q${startX},${centerY - radiusX} ${startX + radiusX},${centerY - radiusX}
                            `}
                            fill="url(#tankBodyH)"
                            stroke="#2f3763"
                            strokeWidth="1.5"
                        />

                        {/* Correct horizontal cylinder liquid fill */}
                        <g clipPath="url(#tankClipH)">
                            <path
                                d={`
                                    M ${startX + R - xOffset - 60} ${liquidY}
                                    L ${endX - R + xOffset + 60} ${liquidY}
                                    L ${endX - R + xOffset + 60} ${centerY + R}
                                    A ${R} ${R} 0 0 1 ${startX + R - xOffset - 60} ${centerY + R}
                                    Z
                                `}
                                fill="url(#liquidFill)"
                                style={{ transition: "all 1s ease" }}
                            />
                        </g>



                        {/* Percent markers */}
                        {/* Vertical level markers (bottom → top) */}
                        {[0, 25, 50, 75, 100].map(level => {
                            const y = centerY + radiusX - (level / 100) * (radiusX * 2);
                            return (
                                <g key={level} opacity="0.7">
                                    <line
                                        x1={startX - 10}
                                        y1={y}
                                        x2={startX - 2}
                                        y2={y}
                                        stroke="#ffffff"
                                        strokeWidth="1.5"
                                    />
                                    <text
                                        x={startX - 14}
                                        y={y + 4}
                                        fill="#ffffff"
                                        fontSize="12"
                                        textAnchor="end"
                                    >
                                        {level}%
                                    </text>
                                </g>
                            );
                        })}

                        {/* Sensor */}
                        <g>
                            <rect x={(startX + endX) / 2 - 6} y={centerY - radiusX - 16} width={12} height={10} fill="#0D2513" />
                            <rect x={(startX + endX) / 2 - 2} y={centerY - radiusX - 6} width={4} height={18} fill="#1a3f2b" />
                        </g>

                        {/* Animated info point & percent box on hover (horizontal) */}
                        {hovered && (
                            <>
                                {/* Animated glowing dot */}
                                <g>
                                    <circle
                                        cx={infoCircleX}
                                        cy={infoCircleY}
                                        r="12"
                                        fill="url(#infoDotGlowLeft)"
                                        opacity="0.88"
                                    >
                                        <animate
                                            attributeName="r"
                                            values="11;16;11"
                                            dur="1.2s"
                                            repeatCount="indefinite"
                                        />
                                        <animate
                                            attributeName="opacity"
                                            values="0.45;0.88;0.45"
                                            dur="1.2s"
                                            repeatCount="indefinite"
                                        />
                                    </circle>

                                    <circle
                                        cx={infoCircleX}
                                        cy={infoCircleY}
                                        r="5"
                                        fill={alertColor ?? "#ffe06c"}
                                        stroke="#d6b100"
                                        strokeWidth="1.6"
                                    >
                                        <animate
                                            attributeName="r"
                                            values="5;7;5"
                                            dur="1.2s"
                                            repeatCount="indefinite"
                                        />
                                    </circle>
                                </g>

                                {/* Info box */}
                                <g filter="url(#boxShadowLeft)">
                                    <rect
                                        x={infoBoxX}
                                        y={infoBoxY}
                                        rx={6}
                                        ry={6}
                                        width={infoBoxWidth}
                                        height={infoBoxHeight}
                                        fill="#22243c"
                                        stroke={alertColor ?? "#ffe06c"}
                                        strokeWidth="1.5"
                                    />
                                    <text
                                        x={infoBoxX + infoBoxWidth / 2}
                                        y={infoBoxY + 25}
                                        textAnchor="middle"
                                        fontSize="19"
                                        fill={alertColor ?? "#ffe06c"}
                                        fontFamily="monospace"
                                        fontWeight="bold"
                                    >
                                        {`${fillPercent}%`}
                                    </text>
                                </g>

                                {/* Inch level */}
                                {inch_level && (
                                    <g filter="url(#boxShadowLeft)">
                                        <text
                                            x={(startX + endX - 30) / 2}
                                            y={infoCircleY * 1.2}
                                            textAnchor="start"
                                            fontSize="19"
                                            fill={alertColor ?? "#ffe06c"}
                                            fontFamily="monospace"
                                            fontWeight="bold"
                                        >
                                            {`${inch_level}"`}
                                        </text>
                                    </g>
                                )}
                            </>
                        )}


                        {/* Labels */}
                        <text
                            x={(startX + endX) / 2}
                            y={centerY + radiusX + 56}
                            fill={dimColor}
                            fontSize={14 * dimTextZoom}
                            textAnchor="middle"
                            fontWeight={hovered ? "bold" : undefined}
                        >
                            Length {length}"
                        </text>

                        <text
                            x={startX + endX + 10}
                            y={centerY}
                            fill={dimColor}
                            fontSize={14 * dimTextZoom}
                            textAnchor="end"
                            dominantBaseline="middle"
                            fontWeight={hovered ? "bold" : undefined}
                        >
                            ⌀ {diameter}"
                        </text>
                    </svg>
                );
            })()}
        </>
    )
}

export default TankVisual